import React, { useState, useEffect } from 'react';
import { createBlog } from '../../../FeatureRedux/blogs/createblogSlice';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { CheckCircle, X } from 'lucide-react';
import { getblog } from '../../../FeatureRedux/blogs/GetBlogReducer';

const Addblogs = ({ onSubmit, onCancel, editIndex, blogs }) => {
  const [formData, setFormData] = useState({
    title: '',
    readTime: '',
    date: '',
    imageFile: null,
    link: '',
  });
  const [errors, setErrors] = useState({});
  const [shake, setShake] = useState(false);
  const dispatch = useDispatch();
  const { loading, isSucces, error } = useSelector((state) => state.createBlog);

  useEffect(() => {
    if (editIndex !== null) {
      const blog = blogs[editIndex];
      setFormData({
        title: blog.title,
        readTime: blog.readTime,
        date: blog.date,
        imageFile: blog.image,
        link: blog.url,
      });
    }
  }, [editIndex, blogs, loading, isSucces]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData((prevData) => ({ ...prevData, imageFile: file }));
      setErrors((prevErrors) => ({ ...prevErrors, imageFile: '' }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        imageFile: 'Please select a valid image file.',
      }));
    }
  };

  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', 'ml_default');

    try {
      const res = await axios.post(
        'https://api.cloudinary.com/v1_1/dnqrumfwn/upload', // Replace with your cloud name
        data
      );
      return res.data.secure_url;
    } catch (err) {
      console.error('Cloudinary Upload Error:', err);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.title) newErrors.title = true;
    if (!formData.readTime) newErrors.readTime = true;
    if (!formData.date) newErrors.date = true;
    if (!formData.imageFile) newErrors.imageFile = true;
    if (!formData.link) newErrors.link = true;

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setShake(true);
      setTimeout(() => setShake(false), 200);
      return;
    }

    let imageUrl = formData.imageFile;

    if (formData.imageFile instanceof File) {
      imageUrl = await uploadToCloudinary(formData.imageFile);
      if (!imageUrl) {
        setErrors((prev) => ({
          ...prev,
          imageFile: 'Image upload failed.',
        }));
        return;
      }
    }

    const blog = {
      title: formData.title,
      readTime: formData.readTime,
      date: formData.date,
      image: imageUrl,
      url: formData.link,
      imageFile: imageUrl,
    };

    await dispatch(createBlog(blog));
    onSubmit(blog, editIndex !== null);
    dispatch(getblog());
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
        <p className="font-bold">Error</p>
        <p>Error creating blog. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Title */}
          <div className="col-span-2">
            <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="title">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              placeholder="Enter blog title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-3 py-2 bg-gray-700 text-white placeholder-white border ${errors.title ? 'border-red-500 animate-shake' : 'border-gray-600'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
            />
            {errors.title && <p className="mt-1 text-xs text-red-500">Title is required</p>}
          </div>

          {/* Read Time */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="readTime">
              Read Time <span className="text-red-500">*</span>
            </label>
            <input

            
              type="text"
              id="readTime"
              placeholder="e.g. 5 min read"
              name="readTime"
              value={formData.readTime}
              onChange={handleChange}
              className={`w-full px-3 py-2 bg-gray-700 text-white placeholder-white border ${errors.readTime ? 'border-red-500 animate-shake' : 'border-gray-600'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
            />
            {errors.readTime && <p className="mt-1 text-xs text-red-500">Read time is required</p>}
          </div>

          {/* Date */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="date">
              Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={`w-full px-3 py-2 bg-gray-700 text-white placeholder-white border ${errors.date ? 'border-red-500 animate-shake' : 'border-gray-600'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
            />
            {errors.date && <p className="mt-1 text-xs text-red-500">Date is required</p>}
          </div>

          {/* Image Upload */}
          <div className="col-span-2">
            <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="image">
              Image <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center">
              <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer bg-gray-700 hover:bg-gray-600 border-gray-600 hover:border-gray-500 transition-colors">
                <div className="flex flex-col items-center justify-center pt-3 pb-4">
                  <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                  <p className="mb-1 text-xs text-gray-400">
                    <span className="font-semibold">Click to upload</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {formData.imageFile ? (formData.imageFile.name || 'Image selected') : 'PNG, JPG, GIF'}
                  </p>
                </div>
                <input 
                  id="image" 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload} 
                  className="hidden" 
                />
              </label>
            </div>
            {errors.imageFile && <p className="mt-1 text-xs text-red-500">Image is required</p>}
          </div>

          {/* Link */}
          <div className="col-span-2">
            <label className="block text-gray-300 text-sm font-medium mb-1" htmlFor="link">
              Link <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="link"
              placeholder="Enter blog URL"
              name="link"
              value={formData.link}
              onChange={handleChange}
              className={`w-full px-3 py-2 bg-gray-700 text-white placeholder-white border ${errors.link ? 'border-red-500 animate-shake' : 'border-gray-600'} rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors`}
            />
            {errors.link && <p className="mt-1 text-xs text-red-500">Link is required</p>}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {editIndex !== null ? 'Update Blog' : 'Add Blog'}
          </button>
        </div>
      </form>

      <style>
        {`
        @keyframes shake {
          0% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          50% { transform: translateX(5px); }
          75% { transform: translateX(-5px); }
          100% { transform: translateX(0); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}
      </style>
    </div>
  );
};

export default Addblogs;




// import React, { useState, useEffect } from 'react';
// import { CheckCircle, X } from 'lucide-react';
// import { createBlog } from '../../../FeatureRedux/blogs/createblogSlice';
// import { useDispatch, useSelector } from 'react-redux';

// const Addblogs = ({ onSubmit, onCancel, editIndex, blogs }) => {
//   const [formData, setFormData] = useState({
//     title: '',
//     readTime: '',
//     date: '',
//     imageFile: null,
//     link: '',
//   });
//   const [errors, setErrors] = useState({});
//   const [shake, setShake] = useState(false);
//   const dispatch = useDispatch();
//   const { loading, isSucces, error } = useSelector((state) => state.createBlog);



//   useEffect(() => {
//     // console.log(`this is blogs data ${JSON.stringify(blogs)}`)
//     if (editIndex !== null) {
//       const blog = blogs[editIndex];
//       setFormData({
//         title: blog.title,
//         readTime: blog.readTime,
//         date: blog.date,
//         imageFile: blog.image, // Use the existing image URL
//         link: blog.url,
//       });
//     }
//   }, [editIndex, blogs, loading, isSucces]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({ ...formData, [name]: value });
//     setErrors({ ...errors, [name]: '' });
//   };







//   const handleImageUpload = (e) => {
//     const file = e.target.files[0];
//     if (file && file.type.startsWith('image/')) {
//       setFormData((prevData) => ({ ...prevData, imageFile: file }));
//       setErrors((prevErrors) => ({ ...prevErrors, imageFile: '' }));
//     } else {
//       setErrors((prevErrors) => ({ ...prevErrors, imageFile: 'Please select a valid image file.' }));
//     }
//   };



//   const handleSubmit = (e) => {
//     e.preventDefault();

//     const newErrors = {};
//     if (!formData.title) newErrors.title = true;
//     if (!formData.readTime) newErrors.readTime = true;
//     if (!formData.date) newErrors.date = true;
//     if (!formData.imageFile) newErrors.imageFile = true;
//     if (!formData.link) newErrors.link = true;

//     setErrors(newErrors);

//     if (Object.keys(newErrors).length > 0) {
//       setShake(true);
//       setTimeout(() => setShake(false), 200); // Shake animation
//       return;
//     }




//   const uploadToCloudinary = async (file) => {
//     const formData = new FormData();
//     formData.append('file', file);
//     formData.append('upload_preset', 'ml_default');

//     try {
//       const response = await axios.post(
//         'https://api.cloudinary.com/v1_1/dziyqo8zo/upload',
//         formData,
//         {
//           headers: { 'Content-Type': 'multipart/form-data' },
//           onUploadProgress: (progressEvent) => {
//             const percentCompleted = Math.round(
//               (progressEvent.loaded * 100) / progressEvent.total
//             );

//             // Update progress
//             setUploadProgress((prevProgress) => ({
//               ...prevProgress,
//               [file.name]: percentCompleted,
//             }));

//             // Remove file if upload is complete
//             if (percentCompleted === 100) {
//               setFiles((prev) =>
//                 prev.filter((item) => item.name !== file.name)
//               );
//             }
//           },
//         }
//       );
//       return response.data.secure_url;
//     } catch (error) {
//       console.error('Error uploading to Cloudinary:', error);
//       return null;
//     }
//   };




//     const blog = {
//       title: formData.title,
//       readTime: formData.readTime,
//       date: formData.date,
//       image: typeof formData.imageFile === 'string' ? formData.imageFile : URL.createObjectURL(formData.imageFile), // Handle existing image URL
//       url: formData.link,
//       imageFile: formData.image, // Pass imageFile for future updates
//     };

//     dispatch(createBlog(blog))
//     onSubmit(blog, editIndex !== null);
//   };

//   // console.log("Blog",blogs )

//   if (loading) {
//     return <h1 className='text-black'>loading...</h1>
//   }
//   if (error) {
//     return <h1>Getting Error in creating project</h1>
//   }

//   return (
//     <div className="flex flex-col items-center p-4">


//       <div className="bg-white p-6 rounded-lg shadow-md w-full">
//         <h2 className="text-2xl font-bold text-center mb-4">{editIndex !== null ? 'Edit Blog' : 'Add Blog'}</h2>

//         <form onSubmit={handleSubmit} className="space-y-4">
//           <input
//             type="text"
//             placeholder="Title"
//             name="title"
//             value={formData.title}
//             onChange={handleChange}
//             className={`w-full p-2 border rounded-md ${errors.title ? 'border-red-500 animate-shake' : ''}`}
//           />

//           <input
//             type="text"
//             placeholder="Read Time"
//             name="readTime"
//             value={formData.readTime}
//             onChange={handleChange}
//             className={`w-full p-2 border rounded-md ${errors.readTime ? 'border-red-500 animate-shake' : ''}`}
//           />

//           <input
//             type="date"
//             name="date"
//             value={formData.date}
//             onChange={handleChange}
//             className={`w-full p-2 border rounded-md ${errors.date ? 'border-red-500 animate-shake' : ''}`}
//           />

//           <input
//             type="file"
//             accept="image/*"
//             onChange={handleImageUpload}
//             className={`w-full p-2 border rounded-md ${errors.imageFile ? 'border-red-500 animate-shake' : ''}`}
//           />

//           {formData.imageFile && (
//             <p className="text-sm text-gray-600">Selected File: {formData.imageFile.name || 'Existing Image'}</p>
//           )}

//           <input
//             type="text"
//             placeholder="Link"
//             name="link"
//             value={formData.link}
//             onChange={handleChange}
//             className={`w-full p-2 border rounded-md ${errors.link ? 'border-red-500 animate-shake' : ''}`}
//           />

//           <button
//             type="submit"
//             className="w-full py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
//           >
//             {editIndex !== null ? 'Update Blog' : 'Submit'}
//           </button>
//         </form>
//       </div>
//       <style>
//         {`
//           @keyframes shake {
//             0% { transform: translateX(0); }
//             25% { transform: translateX(-5px); }
//             50% { transform: translateX(5px); }
//             75% { transform: translateX(-5px); }
//             100% { transform: translateX(0); }
//           }
//           .animate-shake {
//             animation: shake 0.3s ease-in-out;
//           }
//         `}
//       </style>
//     </div>
//   );
// };

// export default Addblogs;


